import React, { useEffect } from "react";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsConditions({ onBack }) {
  useEffect(() => {
    document.title = "Terms & Conditions — HEXA OS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "HEXA OS Terms & Conditions — the rules for using hexawebos.com and its AI-powered apps.");

    // Inject WebPage + BreadcrumbList JSON-LD for rich search snippets
    let script = document.getElementById("hexa-terms-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "hexa-terms-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Terms & Conditions — HEXA OS",
        "url": "https://hexawebos.com/terms",
        "description": "HEXA OS Terms & Conditions — the rules for using hexawebos.com and its AI-powered apps.",
        "inLanguage": "en",
        "isPartOf": { "@type": "WebSite", "name": "HEXA OS", "url": "https://hexawebos.com" },
        "publisher": { "@type": "Organization", "name": "HEXA", "url": "https://hexawebos.com" },
        "dateModified": "2026-02-08"
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home",               "item": "https://hexawebos.com/" },
          { "@type": "ListItem", "position": 2, "name": "Terms & Conditions", "item": "https://hexawebos.com/terms" }
        ]
      }
    ]);
    return () => {
      const el = document.getElementById("hexa-terms-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <div
      className="text-white"
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        background: "#070b1a",
        fontFamily: "'Inter', system-ui",
        WebkitOverflowScrolling: "touch",
      }}
      data-testid="page-terms"
    >
      <div className="max-w-3xl mx-auto px-6 py-14">
        <button
          data-testid="terms-back"
          onClick={onBack}
          className="mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-white flex items-center gap-1 mb-8"
        >
          <ArrowLeft size={12} /> Back to HEXA
        </button>

        <div className="flex items-center gap-3 mb-2">
          <FileText size={22} className="text-[#3b82f6]" />
          <h1 className="text-3xl font-bold">Terms &amp; Conditions</h1>
        </div>
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-8">
          Effective: 8 July 2026 · Last updated: 8 July 2026
        </div>

        <div className="space-y-6 text-[14.5px] leading-relaxed text-white/85">
          <p>
            These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your use of HEXA OS
            (<a className="text-[#a78bfa] underline" href="https://hexawebos.com">hexawebos.com</a>).
            By creating an account or using the service you agree to these Terms.
          </p>

          <Section title="1. Eligibility">
            You must be 13 or older to create a HEXA account. Business accounts
            must be created by an authorized representative of the organization.
          </Section>

          <Section title="2. Account Responsibilities">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You are responsible for keeping your credentials secure.</li>
              <li>Notify us immediately at security@hexawebos.com if you suspect unauthorized access.</li>
              <li>You may not create accounts using automated means or false information.</li>
              <li>One person may hold one Individual account; businesses may create workspaces with multiple seats.</li>
            </ul>
          </Section>

          <Section title="3. Acceptable Use">
            <p>You will NOT use HEXA OS to:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Send spam, unsolicited bulk email, phishing or fraudulent messages.</li>
              <li>Distribute malware, viruses, or any harmful code.</li>
              <li>Violate any law, including India IT Act 2000, TRAI regulations, GDPR, CAN-SPAM.</li>
              <li>Infringe intellectual property rights of others.</li>
              <li>Harass, threaten or defame any person.</li>
              <li>Reverse engineer, decompile or attempt to extract source code.</li>
              <li>Overload our infrastructure or bypass rate limits.</li>
            </ul>
          </Section>

          <Section title="4. Subscription & Billing">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><b>Free tier</b>: 5 GB storage, 100 AI credits/month, all apps included.</li>
              <li><b>Pro</b>: ₹150 + 18% GST/month (≈ ₹177), 50 GB storage, 1,000 AI credits.</li>
              <li><b>Business</b>: contact us for team pricing.</li>
              <li>Prices are in INR, inclusive of applicable GST as per Indian law.</li>
              <li>Referral coins may be applied against paid plans as promotional credit.</li>
              <li>Paid subscriptions renew automatically; cancel any time from My Profile.</li>
              <li>Refunds: pro-rated refund on annual plans within 7 days of purchase. No refund on monthly plans.</li>
            </ul>
          </Section>

          <Section title="5. AI Features">
            <p>
              HEXA integrates third-party LLMs (Claude, GPT, Gemini) via the
              Emergent LLM key. AI outputs are generated statistically and may
              contain errors. You are responsible for reviewing AI-generated
              content before acting on it. We disclaim liability for any decision
              made solely on AI output.
            </p>
          </Section>

          <Section title="6. External Email Accounts">
            When you connect an external mailbox (Gmail, Yahoo, custom IMAP),
            HEXA acts as a mail client. You are responsible for complying with
            your email provider&apos;s Terms of Service. HEXA is not liable for
            deliverability, blacklisting, or provider-side actions.
          </Section>

          <Section title="7. Intellectual Property">
            HEXA owns the software, brand, design and trademarks. You retain full
            ownership of the content you create with HEXA — we claim no rights
            to your emails, documents, files or data.
          </Section>

          <Section title="8. Termination">
            We may suspend or terminate your account for violation of these
            Terms. On termination we delete your data within 30 days (subject
            to legal retention obligations).
          </Section>

          <Section title="9. Disclaimer & Limitation of Liability">
            HEXA OS is provided &ldquo;as is&rdquo; without warranties of any kind. To the
            maximum extent permitted by law, our aggregate liability is limited
            to the fees you paid in the past 12 months.
          </Section>

          <Section title="10. Governing Law">
            These Terms are governed by the laws of India. Disputes are subject
            to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.
          </Section>

          <Section title="11. Contact">
            <div>Email: <a className="text-[#a78bfa] underline" href="mailto:legal@hexawebos.com">legal@hexawebos.com</a></div>
            <div>Support: <a className="text-[#a78bfa] underline" href="mailto:team@hexawebos.com">team@hexawebos.com</a></div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-white mb-2">{title}</h2>
      <div className="text-white/80">{children}</div>
    </section>
  );
}
